import { useState } from 'react'
import { SignatureConnexion } from '../Types/Class/SignatureConnexion'
import BackOfficeAPIClient from '../../../../clients/BackofficeAPIClient'
import { Site } from '../../../../types/Site'

export interface UseConnectionsManagementReturn {
    connections: SignatureConnexion[]
    setConnections: (connections: SignatureConnexion[]) => void
    testingConnection: number | null
    handleUpdateConnection: (siteId: number, field: keyof SignatureConnexion, value: any) => void
    handleTestConnection: (siteId: number, appName: string) => Promise<void>
    handleToggleConnection: (siteId: number) => void
    getConnectionToggleClass: (status?: 'inactive' | 'active' | 'error') => string
    resetConnections: () => void
    loadConnections: (sites: Site[], existingConnections?: SignatureConnexion[]) => void
}

export function useConnectionsManagement(
    setHasChanges: (value: boolean) => void
): UseConnectionsManagementReturn {
    const backofficeApiClient = new BackOfficeAPIClient()
    const [connections, setConnections] = useState<SignatureConnexion[]>([])
    const [testingConnection, setTestingConnection] = useState<number | null>(null)

    const handleUpdateConnection = (siteId: number, field: keyof SignatureConnexion, value: any) => {
        setConnections(prev => prev.map(c =>
            c.IDSite === siteId ? { ...c, [field]: value } : c
        ))
        setHasChanges(true)
    }

    const handleTestConnection = async (siteId: number, appName: string) => {
        const connection = connections.find(c => c.IDSite === siteId)
        if (!connection || !connection.IDPeopleRepresentant || !connection.Token) {
            alert('Veuillez remplir le représentant et le token avant de tester')
            return
        }

        setTestingConnection(siteId)
        try {
            handleUpdateConnection(siteId, 'TestStatus', 'inactive')

            const result = await backofficeApiClient.post<any>(`/interfaces/signatures/${appName}/connexion/ping`, {
                token: connection.Token,
                siteId: siteId
            })

            if (result.success) {
                handleUpdateConnection(siteId, 'TestStatus', 'active')
            } else {
                handleUpdateConnection(siteId, 'TestStatus', 'error')
            }
        } catch (error) {
            console.error('Erreur lors du test de connexion:', error)
            handleUpdateConnection(siteId, 'TestStatus', 'error')
        } finally {
            setTestingConnection(null)
        }
    }

    const handleToggleConnection = (siteId: number) => {
        setConnections(prev => prev.map(c =>
            c.IDSite === siteId ? { ...c, EstActif: !c.EstActif } : c
        ))
        setHasChanges(true)
    }

    const getConnectionToggleClass = (status?: 'inactive' | 'active' | 'error') => {
        switch (status) {
            case 'active': return 'btn-success'
            case 'error': return 'btn-danger'
            default: return 'btn-secondary'
        }
    }

    const resetConnections = () => {
        setConnections([])
        setTestingConnection(null)
    }

    const loadConnections = (sites: Site[], existingConnections?: SignatureConnexion[]) => {
        const connectionsData = sites.map(site => {
            const existingConnection = existingConnections?.find(
                (c) => c.IDSite === site.IDSite
            )

            return new SignatureConnexion({
                ...existingConnection,
                IDSite: site.IDSite,
            })
        })
        setConnections(connectionsData)
    }

    return {
        connections,
        setConnections,
        testingConnection,
        handleUpdateConnection,
        handleTestConnection,
        handleToggleConnection,
        getConnectionToggleClass,
        resetConnections,
        loadConnections,
    }
}
